<script>
// Notifications live in admission-usat-api and are read by my-usat-profile through the same rows.
// This page used to call app.usat.uz with a webhook secret hardcoded in the bundle; that service is
// gone, so every request 404'd into the SPA's index.html and the list silently stayed empty. These
// paths go through the panel's own axios instance, which carries the admin token and base URL.
const ENDPOINTS = {
  LIST: `/admin/news`,
  DETAIL: (id) => `/admin/news/${id}`,
  CREATE: `/admin/news`,
  UPDATE: (id) => `/admin/news/${id}`,
  DELETE: (id) => `/admin/news/${id}`,
  SPECIALITIES: `/admin/news/specialities`,
};

// Helpers
function safeJson(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  if (v.results) return v.results;
  if (v.data) return v.data;
  if (v.items) return v.items;
  return [];
}

function pickIdName(x) {
  const rawId = x?.id ?? x?.value ?? x?.code ?? x?.Id ?? x?.ID;
  const idNum = Number.isFinite(Number(rawId)) ? Number(rawId) : String(rawId);
  return {
    id: idNum,
    name: x?.name ?? x?.title ?? x?.label ?? String(rawId ?? ""),
  };
}

// UI label map (architecture-ready translations). Falls back to 'uz' if no i18n.
const LABELS = {
  format: {
    daytime: { uz: "Kunduzgi (daytime)", ru: "Дневная", en: "Daytime" },
    external: { uz: "Sirtqi (external)", ru: "Заочная", en: "External" },
    evening: { uz: "Kechki (evening)", ru: "Вечерняя", en: "Evening" },
  },
  language: {
    uzbek: { uz: "O‘zbek (uzbek)", ru: "Узбекский", en: "Uzbek" },
    russian: { uz: "Rus (russian)", ru: "Русский", en: "Russian" },
  },
  // admission's PaidStatus enum, not the old app.usat.uz vocabulary — these exact values are what
  // the audience matcher compares a contract's paid_status against.
  paid_status: {
    NOT_PAID: { uz: "To‘lanmagan", ru: "Не оплачено", en: "Not paid" },
    HALF_PAID: { uz: "Yarim to‘lov", ru: "Частично оплачено", en: "Partially paid" },
    PAID: { uz: "To‘langan", ru: "Оплачено", en: "Paid" },
    REFUNDED: { uz: "Qaytarilgan", ru: "Возвращено", en: "Refunded" },
  },
};

import axios from "axios";

export default {
  name: "NewsWebhookManager",

  data: () => ({
    canCreate: false,
    canUpdate: false,
    canDelete: false,
    // Loading flags
    loadingList: false,
    loadingSpecialities: false,
    pending: false,

    // Toast
    toast: { visible: false, text: "" },

    // Data
    items: [], // list of news
    specialities: [], // [{id, name}]

    // UI State
    viewItem: null, // object for detail drawer
    showForm: false, // create/edit modal toggle
    formMode: "create", // or "edit"

    // Active language tab for message inputs
    formLang: "uz",

    // Form model
    form: {
      id: null,
      text_uz: "",
      text_ru: "",
      text_en: "",
      study_year: [1, 2, 3, 4],
      speciality: [],
      study_format: ["daytime", "external", "evening"],
      study_language: ["uzbek", "russian"],
      payment_status: ["PARTIALLY_PAID", "FULLY_PAID"],
    },
    confirmDelete: { open: false, id: null, loading: false },
  }),

  created() {
    this.loadSpecialities({ silent: true });
    this.loadList({ silent: true });
  },

  async mounted() {
    this.canCreate = await this.hasRole("XABARLAR", "CREATE");
    this.canUpdate = await this.hasRole("XABARLAR", "UPDATE");
    this.canDelete = await this.hasRole("XABARLAR", "DELETE");
  },

  computed: {
    // current UI locale
    uiLocale() {
      const raw = this.$i18n?.locale?.value ?? this.$i18n?.locale ?? "uz";
      const s = typeof raw === "string" ? raw : String(raw || "uz");
      return s.slice(0, 2);
    },

    // Options built from LABELS (auto-translate)
    formatOptionsUi() {
      return Object.entries(LABELS.format).map(([value, dict]) => ({ value, label: dict[this.uiLocale] || dict.uz }));
    },
    languageOptionsUi() {
      return Object.entries(LABELS.language).map(([value, dict]) => ({ value, label: dict[this.uiLocale] || dict.uz }));
    },
    paidStatusOptionsUi() {
      return Object.entries(LABELS.paid_status).map(([value, dict]) => ({ value, label: dict[this.uiLocale] || dict.uz }));
    },

    // character counter based on active language
    activeText: {
      get() { return this.form[`text_${this.formLang}`]; },
      set(v) { this.form[`text_${this.formLang}`] = v; },
    },
    activeCount() { return (this.activeText || "").length; },
    canSubmit() {
      const lengthsOk = ["uz", "ru", "en"].every((k) => (this.form[`text_${k}`] || "").length <= 160);
      const hasAnyText = ["uz", "ru", "en"].some((k) => (this.form[`text_${k}`] || "").trim().length > 0);
      return hasAnyText && lengthsOk && !this.pending;
    },
    formTitle() {
      return this.formMode === "create" ? (this.$t ? this.$t("Create news") : "Create news") : (this.$t ? this.$t("Edit news") : "Edit news");
    },
  },

  methods: {
    async hasRole(panel, op) {
      try {
        const res = await axios.get(`/admin/panel-permissions/check`, {
          params: { appPanel: panel, op }
        });
        return res.data === true;
      } catch {
        return false;
      }
    },

    t(s) { return this.$t ? this.$t(s) : s; },

    // --- Label helpers (show labels while keeping raw values in payload) ---
    labelFormat(v) { const d = LABELS.format?.[v]; return d ? (d[this.uiLocale] || d.uz) : String(v); },
    labelLanguage(v) { const d = LABELS.language?.[v]; return d ? (d[this.uiLocale] || d.uz) : String(v); },
    labelPaidStatus(v) { const d = LABELS.paid_status?.[v]; return d ? (d[this.uiLocale] || d.uz) : String(v); },

    // --- API core ---
    async apiGet(url) {
      const res = await axios.get(url);
      return res.data;
    },
    async apiSend(url, method, body) {
      const res = await axios.request({ url, method, data: body });
      return res.data ?? {};
    },

    // --- Toast ---
    showToast(text) {
      this.toast.text = text;
      this.toast.visible = true;
      setTimeout(() => (this.toast.visible = false), 3000);
    },

    // --- Loaders ---
    async loadList({ silent = false } = {}) {
      try {
        this.loadingList = true;
        const json = await this.apiGet(ENDPOINTS.LIST);
        this.items = safeJson(json).map(this.normalizeItem).sort((a,b)=>{
          const au = new Date(a.updated_at || a.created_at || 0).getTime();
          const bu = new Date(b.updated_at || b.created_at || 0).getTime();
          return bu - au;
        });
      } catch (e) {
        console.error(e);
        if (!silent) this.showToast(this.t("Failed to load messages"));
      } finally {
        this.loadingList = false;
      }
    },

    async loadSpecialities({ silent = false } = {}) {
      try {
        this.loadingSpecialities = true;
        const json = await this.apiGet(ENDPOINTS.SPECIALITIES);
        const list = safeJson(json).map(pickIdName).filter((x) => x.id != null);
        // dedupe by id
        const seen = new Set();
        this.specialities = list.filter((x) => (seen.has(x.id) ? false : (seen.add(x.id), true)));
        // if editing/creating, make sure selected ids are valid
        const valid = new Set(this.specialities.map((s) => s.id));
        this.form.speciality = (this.form.speciality || []).filter((id) => valid.has(id));
      } catch (e) {
        console.error(e);
        if (!silent) this.showToast(this.t("Failed to load specialities"));
      } finally {
        this.loadingSpecialities = false;
      }
    },

    normalizeItem(raw) {
      const numArr = (arr) => (Array.isArray(arr) ? arr.map(v => (Number.isFinite(Number(v)) ? Number(v) : v)) : []);
      return {
        id: raw?.id ?? raw?.pk ?? raw?.Id,
        text_uz: raw?.text_uz ?? raw?.textUz ?? "",
        text_ru: raw?.text_ru ?? raw?.textRu ?? "",
        text_en: raw?.text_en ?? raw?.textEn ?? "",
        study_year: numArr(raw?.study_year ?? raw?.course),
        speciality: numArr(raw?.speciality),
        study_format: Array.isArray(raw?.study_format) ? raw.study_format : [],
        study_language: Array.isArray(raw?.study_language) ? raw.study_language : [],
        payment_status: Array.isArray(raw?.payment_status) ? raw.payment_status : [],
        created_at: raw?.created_at ?? raw?.createdAt ?? null,
        updated_at: raw?.updated_at ?? raw?.updatedAt ?? null,
      };
    },

    // --- UI helpers ---
    tagClass(kind) { return `tag tag--${kind}`; },
    formatDateISO(s) { if (!s) return ""; try { return new Date(s).toLocaleString(); } catch { return String(s); } },
    specialityName(id) { return this.specialities.find((s) => String(s.id) === String(id))?.name ?? `#${id}`; },

    // --- Chip helpers ---
    toggleArr(target, v) {
      const arr = this.form[target];
      const idx = arr.indexOf(v);
      if (idx === -1) arr.push(v); else arr.splice(idx, 1);
      this.form[target] = [...arr];
    },
    setAll(target, values) { this.form[target] = [...values]; },
    clearAll(target) { this.form[target] = []; },

    // --- CRUD Flows ---
    openCreate() {
      this.formMode = "create";
      this.form = {
        id: null,
        text_uz: "",
        text_ru: "",
        text_en: "",
        study_year: [1, 2, 3, 4],
        speciality: [],
        study_format: ["daytime", "external", "evening"],
        study_language: ["uzbek", "russian"],
        payment_status: ["NOT_PAID", "HALF_PAID", "PAID"],
      };
      this.formLang = "uz";
      this.showForm = true;
    },

    openEdit(item) {
      this.formMode = "edit";
      this.form = { ...item };
      this.formLang = "uz";
      this.showForm = true;
    },

    openDetail(item) {
      this.viewItem = item;
    },

    askDelete(item) {
      this.confirmDelete.id = item.id;
      this.confirmDelete.loading = false;
      this.confirmDelete.open = true;
    },

    async doDelete() {
      const id = this.confirmDelete.id;
      if (!id) return;
      try {
        this.confirmDelete.loading = true;
        await this.apiSend(ENDPOINTS.DELETE(id), "DELETE");
        this.showToast(this.t("Deleted"));
        this.items = this.items.filter((x) => x.id !== id);
      } catch (e) {
        console.error(e);
        this.showToast(this.t("Failed to delete"));
      } finally {
        this.confirmDelete.loading = false;
        this.confirmDelete.open = false;
        this.confirmDelete.id = null;
      }
    },

    async submitForm() {
      if (!this.canSubmit) return;
      const toNumArr = (arr) => (Array.isArray(arr) ? arr.map(v => (Number.isFinite(Number(v)) ? Number(v) : v)) : []);
      const payload = {
        text_uz: (this.form.text_uz || "").trim(),
        text_ru: (this.form.text_ru || "").trim(),
        text_en: (this.form.text_en || "").trim(),
        study_year: toNumArr(this.form.study_year),
        speciality: toNumArr(this.form.speciality),
        study_format: [...this.form.study_format],
        study_language: [...this.form.study_language],
        payment_status: [...this.form.payment_status],
      };

      try {
        this.pending = true;
        if (this.formMode === "create") {
          await this.apiSend(ENDPOINTS.CREATE, "POST", payload);
          this.showToast(this.t("Created successfully"));
          this.showForm = false;
          await this.loadList();
        } else {
          try {
            await this.apiSend(ENDPOINTS.UPDATE(this.form.id), "PATCH", payload);
          } catch (e) {
            // some servers only allow PUT
            await this.apiSend(ENDPOINTS.UPDATE(this.form.id), "PUT", payload);
          }
          this.showToast(this.t("Updated successfully"));
          this.showForm = false;
          await this.loadList();
        }
      } catch (e) {
        console.error(e);
        this.showToast(this.t("Failed to save") + (e?.message ? ` — ${e.message}` : ""));
      } finally {
        this.pending = false;
      }
    },
  },
};
</script>

<template>
  <v-container>
    <div class="page">
      <!-- Toast -->
      <transition name="fade-slide">
        <div v-if="toast.visible" class="toast" role="status">
          <div class="toast-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="white" stroke-width="2" />
              <path d="M12 16V12" stroke="white" stroke-width="2" stroke-linecap="round" />
              <circle cx="12" cy="8" r="1" fill="white" />
            </svg>
          </div>
          <div class="toast-content">{{ toast.text }}</div>
          <button class="toast-close" @click="toast.visible = false" aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18" stroke="white" stroke-width="2" stroke-linecap="round" />
              <path d="M6 6L18 18" stroke="white" stroke-width="2" stroke-linecap="round" />
            </svg>
          </button>
        </div>
      </transition>

      <!-- Header / Toolbar -->
      <header class="toolbar">
        <div class="left">
          <h1 class="title">{{ t('News messages') }}</h1>
          <button class="ghost with-icon" @click="loadList" :disabled="loadingList">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-width="2" d="M4 4v6h6M20 20v-6h-6M20 8a8 8 0 10-7.45 7.96"/></svg>
            <span v-if="!loadingList">{{ t('Refresh') }}</span>
            <span v-else class="spinner-sm" aria-hidden="true"></span>
          </button>
        </div>
        <div class="right">
          <button v-if="canCreate" class="btn-primary with-icon font-made" @click="openCreate">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-width="2" d="M12 5v14M5 12h14"/></svg>
            {{ t('New message') }}
          </button>
        </div>
      </header>

      <!-- List: each item on a new line -->
      <section class="list" :aria-busy="loadingList">
        <div v-if="loadingList" class="loading-wrap">
          <div class="spinner" aria-hidden="true"></div>
        </div>

        <div v-else-if="items.length === 0" class="empty">
          <svg
            class="empty-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          <p>{{ t('No messages yet') }}</p>
        </div>

        <div v-else class="cards">
          <article v-for="item in items" :key="item.id" class="card">
            <div class="card-top">
              <div class="card-text">
                <p class="text-main">{{ item.text_uz || item.text_ru || item.text_en || '—' }}</p>
              </div>
              <div class="card-actions">
                <button class="icon-btn view" @click="openDetail(item)">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                  {{ t('View') }}
                </button>
                <button v-if="canUpdate" class="icon-btn edit" @click="openEdit(item)">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M11 5h2m-1-1v2m9 7h-2m-1 1v-2M7 19h10M5 7h14M5 11h14M5 15h14"/></svg>
                  {{ t('Edit') }}
                </button>
                <button v-if="canDelete" class="icon-btn delete" @click="askDelete(item)">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                  {{ t('Delete') }}
                </button>
              </div>
            </div>

            <div class="tags">
              <div class="tag-group">
                <span class="tag-label">{{ t('Course') }}:</span>
                <span v-for="c in item.study_year" :key="c" :class="tagClass('course')">{{ c }}</span>
              </div>
              <div class="tag-group">
                <span class="tag-label">{{ t('Format') }}:</span>
                <span v-for="f in item.study_format" :key="f" :class="tagClass('format')">{{ labelFormat(f) }}</span>
              </div>
              <div class="tag-group">
                <span class="tag-label">{{ t('Language') }}:</span>
                <span v-for="l in item.study_language" :key="l" :class="tagClass('lang')">{{ labelLanguage(l) }}</span>
              </div>
              <div class="tag-group">
                <span class="tag-label">{{ t('Status') }}:</span>
                <span v-for="p in item.payment_status" :key="p" :class="tagClass('paid')">{{ labelPaidStatus(p) }}</span>
              </div>
              <div class="tag-group">
                <span class="tag-label">{{ t('Specialities') }}:</span>
                <span v-for="s in (item.speciality || [])" :key="s" :class="tagClass('spec')">{{ specialityName(s) }}</span>
                <span v-if="!item.speciality || item.speciality.length===0" class="muted">—</span>
              </div>
            </div>
          </article>
        </div>
      </section>

      <!-- Detail Drawer -->
      <transition name="zoom-fade">
        <div v-if="viewItem" class="modal" role="dialog" aria-modal="true">
          <!-- Blurred background -->
          <div class="backdrop" @click="viewItem = null"></div>

          <!-- Dialog -->
          <div class="drawer">
            <div class="drawer-header">
              <h2>{{ t('Message details') }}</h2>
              <button class="icon" @click="viewItem = null" aria-label="Close">✕</button>
            </div>

            <div class="drawer-body">
              <div class="kv"><span>UZ</span><p>{{ viewItem.text_uz || '—' }}</p></div>
              <div class="kv"><span>RU</span><p>{{ viewItem.text_ru || '—' }}</p></div>
              <div class="kv"><span>EN</span><p>{{ viewItem.text_en || '—' }}</p></div>

              <div class="hr"></div>
              <div class="kv"><span>{{ t('Course') }}</span><p>{{ (viewItem.study_year||[]).join(', ') || '—' }}</p></div>
              <div class="kv"><span>{{ t('Format') }}</span><p>{{ (viewItem.study_format||[]).map(labelFormat).join(', ') || '—' }}</p></div>
              <div class="kv"><span>{{ t('Language') }}</span><p>{{ (viewItem.study_language||[]).map(labelLanguage).join(', ') || '—' }}</p></div>
              <div class="kv"><span>{{ t('Status') }}</span><p>{{ (viewItem.payment_status||[]).map(labelPaidStatus).join(', ') || '—' }}</p></div>
              <div class="kv"><span>{{ t('Specialities') }}</span>
                <p>
                  <template v-if="viewItem.speciality && viewItem.speciality.length">
                    <span v-for="s in viewItem.speciality" :key="s" class="chip">{{ specialityName(s) }}</span>
                  </template>
                  <span v-else>—</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </transition>

      <!-- Create/Edit Modal -->
      <transition name="zoom-fade">
        <div v-if="showForm" class="modal" role="dialog" aria-modal="true" @click.self="showForm = false">
          <div class="modal-card">
              <div class="modal-header">
                <h3>{{ formTitle }}</h3>
                <button class="icon" @click="showForm = false" aria-label="Close">✕</button>
              </div>

              <div class="modal-body">
                <!-- Tabs for languages -->
                <div class="tabs">
                  <button :class="['tab', formLang==='uz' ? 'active' : '']" @click="formLang='uz'">UZ</button>
                  <button :class="['tab', formLang==='ru' ? 'active' : '']" @click="formLang='ru'">RU</button>
                  <button :class="['tab', formLang==='en' ? 'active' : '']" @click="formLang='en'">EN</button>
                  <span class="muted ml-auto">{{ activeCount }}/160</span>
                </div>

                <textarea
                    v-model="activeText"
                    rows="4"
                    maxlength="160"
                    class="textarea"
                    :placeholder="t('Type your message (max 160)')"
                ></textarea>

                <!-- Course -->
                <div class="group">
                  <label class="label">{{ t('Course') }}</label>
                  <div class="options">
                    <button v-for="c in [1,2,3,4]" :key="c" type="button" @click="toggleArr('study_year', c)" :class="['option', form.study_year.includes(c) ? 'option--active' : '']">{{ c }}</button>
                    <button type="button" class="option option--ghost" @click="setAll('study_year', [1,2,3,4])">{{ t('All') }}</button>
                    <button type="button" class="option option--ghost" @click="clearAll('study_year')">{{ t('Clear') }}</button>
                  </div>
                </div>

                <!-- Study Format -->
                <div class="group">
                  <label class="label">{{ t('Study format') }}</label>
                  <div class="options">
                    <button v-for="f in formatOptionsUi" :key="f.value" type="button" @click="toggleArr('study_format', f.value)" :class="['option', form.study_format.includes(f.value) ? 'option--active' : '']">{{ f.label }}</button>
                    <button type="button" class="option option--ghost" @click="setAll('study_format', formatOptionsUi.map(x=>x.value))">{{ t('All') }}</button>
                    <button type="button" class="option option--ghost" @click="clearAll('study_format')">{{ t('Clear') }}</button>
                  </div>
                </div>

                <!-- Study Language -->
                <div class="group">
                  <label class="label">{{ t('Study language') }}</label>
                  <div class="options">
                    <button v-for="l in languageOptionsUi" :key="l.value" type="button" @click="toggleArr('study_language', l.value)" :class="['option', form.study_language.includes(l.value) ? 'option--active' : '']">{{ l.label }}</button>
                    <button type="button" class="option option--ghost" @click="setAll('study_language', languageOptionsUi.map(x=>x.value))">{{ t('All') }}</button>
                    <button type="button" class="option option--ghost" @click="clearAll('study_language')">{{ t('Clear') }}</button>
                  </div>
                </div>

                <!-- Paid Status -->
                <div class="group">
                  <label class="label">{{ t('Paid status') }}</label>
                  <div class="options">
                    <button v-for="p in paidStatusOptionsUi" :key="p.value" type="button" @click="toggleArr('payment_status', p.value)" :class="['option', form.payment_status.includes(p.value) ? 'option--active' : '']">{{ p.label }}</button>
                    <button type="button" class="option option--ghost" @click="setAll('payment_status', paidStatusOptionsUi.map(x=>x.value))">{{ t('All') }}</button>
                    <button type="button" class="option option--ghost" @click="clearAll('payment_status')">{{ t('Clear') }}</button>
                  </div>
                </div>

                <!-- Speciality -->
                <div class="group">
                  <label class="label">{{ t('Speciality') }}</label>
                  <div class="checkbox-wrap">
                    <div class="checkbox-grid">
                      <label v-for="s in specialities" :key="s.id" class="checkbox-item">
                        <input type="checkbox" class="checkbox-input" :value="s.id" v-model="form.speciality" />
                        <span class="checkbox-label">{{ s.name }}</span>
                      </label>
                    </div>
                    <div class="select-actions">
                      <button type="button" class="option option--ghost w-full" @click="setAll('speciality', specialities.map(s=>s.id))" :disabled="loadingSpecialities || specialities.length===0">{{ t('Select all') }}</button>
                      <button type="button" class="option option--ghost w-full" @click="clearAll('speciality')" :disabled="loadingSpecialities || specialities.length===0">{{ t('Clear') }}</button>
                      <small v-if="loadingSpecialities" class="hint">{{ t('Loading...') }}</small>
                      <small v-else class="hint">{{ t('Selected') }}: {{ form.speciality.length }}</small>
                    </div>
                  </div>
                </div>
              </div>

              <div class="modal-footer">
                <button class="ghost" @click="showForm=false">{{ t('Cancel') }}</button>
                <button class="btn-primary" :disabled="!canSubmit || pending" @click="submitForm">
                  <span v-if="!pending">{{ formMode==='create' ? t('Create') : t('Save') }}</span>
                  <span v-else class="spinner-sm" aria-hidden="true"></span>
                </button>
              </div>
          </div>
        </div>
      </transition>

      <!-- Delete Confirm -->
      <transition name="zoom-fade">
        <div v-if="confirmDelete?.open" class="modal" role="dialog" aria-modal="true" @click.self="confirmDelete.open = false">
          <div class="modal-card small">
            <div class="modal-header">
              <h3>{{ t('Delete message') }}</h3>
            </div>
            <div class="modal-body">
              <p>{{ t('Are you sure you want to delete this message? This action cannot be undone.') }}</p>
            </div>
            <div class="modal-footer">
              <button class="ghost" @click="confirmDelete.open=false" :disabled="confirmDelete.loading">{{ t('Cancel') }}</button>
              <button class="danger" @click="doDelete" :disabled="confirmDelete.loading">
                <span v-if="!confirmDelete.loading">{{ t('Delete') }}</span>
                <span v-else class="spinner-sm" aria-hidden="true"></span>
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </v-container>
</template>

<style scoped>
/* Page sizing per request */
.page { max-width: 1280px; margin: 0 auto; padding: 0 16px; }

/* Apple system font — forced with !important to override Vuetify's
   ".v-application .title { font-family: Roboto !important }" etc. */
.page,
.page * {
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif !important;
}

.title { font-size: 1.75rem; font-weight: 700; color: #111827; }
.toolbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin: 12px 0 16px; }
.toolbar .left { display: flex; align-items: center; gap: 10px; }
.toolbar .right { display: flex; gap: 8px; }

/* Buttons */
button, input, textarea, select { cursor: pointer; }
.btn-primary, .danger, .ghost, .tiny { border: none; border-radius: 12px; padding: 10px 14px; font-weight: 600; transition: background .15s ease, border-color .15s ease, color .15s ease; display: inline-flex; align-items: center; gap: 8px; }
.btn-primary { height: 41px; background: #00274c; color: #fff; }
.btn-primary:hover { opacity: .9; }
.danger { background: #dc2626; color: #fff; }
.danger:hover { opacity: .9; }
.ghost { background: #fff; color: #111827; border: 1.5px dashed #d1d5db; height: 41px; }
.ghost:hover { border-color: #00274c; color: #00274c; }
.with-icon svg { width: 16px; height: 16px; }

/* List: one card per line */
.list { min-height: 160px; }
.loading-wrap { display: grid; place-items: center; padding: 40px 0; }
.empty { display: grid; place-items: center; gap: 10px; padding: 48px 0; color: #6b7280; }
.empty-icon { width: 44px; height: 44px; opacity: .55; }
.empty p { margin: 0; font-size: 14px; }
.cards { display: flex; flex-direction: column; gap: 16px; }
.card { background: #fff; border: 1px solid #e5e7eb; border-radius: 16px; padding: 14px; box-shadow: 0 4px 10px rgba(0,0,0,.04); display: flex; flex-direction: column; gap: 12px; }
.card-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.card-text { display: grid; gap: 4px; }
.text-main { font-size: 1.05rem; color: #111827; }
.muted { color: #6b7280; }
.card-actions { display: flex; gap: 8px; flex-wrap: wrap; }

/* Icon action buttons */
.icon-btn { display: inline-flex; align-items: center; gap: 6px; font-weight: 600; padding: 8px 12px; border-radius: 10px; border: none; cursor: pointer; }
.icon-btn svg { width: 16px; height: 16px; }
.icon-btn.view { background: #f3f4f6; color: #111827; }
.icon-btn.edit { background: #eef2ff; color: #1e3a8a; }
.icon-btn.delete { background: #fee2e2; color: #b91c1c; }
.icon-btn:hover { opacity: 0.92; }

/* Tags */
.tags { display: grid; gap: 8px; margin-top: 6px; }
.tag-group { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }
.tag-label { font-size: .85rem; color: #6b7280; margin-right: 4px; }
.tag { background: #f3f4f6; border: 1px solid #e5e7eb; color: #111827; font-size: .8rem; padding: 4px 8px; border-radius: 9999px; }
.tag--course { background: #eef2ff; border-color: #e0e7ff; }
.tag--format { background: #ecfeff; border-color: #cffafe; }
.tag--lang { background: #f0fdf4; border-color: #dcfce7; }
.tag--paid { background: #fff7ed; border-color: #ffedd5; }
.tag--spec { background: #f9fafb; }

/* === Transition === */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.25s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* === Blurred backdrop === */
.backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 0;
}

/* === Centered dialog === */
.drawer {
  position: relative;
  z-index: 1;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  padding: 20px 24px;
  max-width: 720px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
}

/* === Header === */
.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.drawer-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.drawer-header .icon {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #6b7280;
  transition: color 0.2s ease;
}
.drawer-header .icon:hover {
  color: #111827;
}

/* === Body === */
.drawer-body {
  display: grid;
  gap: 10px;
}

.kv {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 10px;
  align-items: start;
}

.kv span {
  color: #6b7280;
  font-size: 14px;
}

.kv p {
  margin: 0;
  color: #111827;
  font-size: 15px;
  line-height: 1.4;
}

.hr {
  height: 1px;
  background: #e5e7eb;
  margin: 10px 0;
}

/* === Chips === */
.chip {
  display: inline-block;
  background: #f3f4f6;
  color: #374151;
  padding: 2px 8px;
  border-radius: 8px;
  margin: 2px 4px 2px 0;
  font-size: 13px;
}

/* === Animation === */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}


/* Modal */
.modal { position: fixed; inset: 0; background: rgba(0,0,0,.35); backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px); display: grid; place-items: center; padding: 20px; z-index: 100; }
.modal-card { width: 100%; max-width: 820px; max-height: 85vh; background: #fff; border-radius: 16px; border: 1px solid #e5e7eb; box-shadow: 0 10px 30px rgba(0,0,0,.12); display: flex; flex-direction: column; overflow: hidden; }
.modal-card.small { max-width: 480px; }
.modal-header { display: flex; align-items: center; justify-content: space-between; padding: 14px 20px; border-bottom: 1px solid #e5e7eb; flex-shrink: 0; }
.modal-body { padding: 20px; display: grid; gap: 16px; overflow-y: auto; min-height: 0; }
.modal-footer { display: flex; justify-content: flex-end; gap: 10px; padding: 14px 20px; border-top: 1px solid #e5e7eb; flex-shrink: 0; }
.icon { background: transparent; border: none; font-size: 18px; width: 36px; height: 36px; display: inline-grid; place-items: center; border-radius: 50%; }
.icon:hover { background: #f3f4f6; }

/* Inputs */
.textarea { width: 100%; min-height: 100px; border: 1px solid #d1d5db; border-radius: 12px; padding: 12px; font-size: 1rem; resize: vertical; background: transparent; }
.textarea:focus { outline: none; border-color: #00274c; }
.group { display: grid; gap: 6px; }
.label { font-size: .9rem; color: #4b5563; }
.options { display: flex; flex-wrap: wrap; gap: 8px; }
.option { padding: 8px 14px; font-size: .88rem; font-weight: 600; color: #374151; background: #f3f4f6; border: 1.5px solid transparent; border-radius: 9999px; }
.option:not(.option--active):hover { border: 1.5px solid #001f43; color: #001f43; }
.option--active { background: #00274c; color: #fff; }
.option.option--ghost { background: #fff; border: 1.5px dashed #d1d5db; color: #374151; }
.option.option--ghost:hover { border-color: #001f43; }

/* Tabs */
.tabs { display: flex; align-items: center; gap: 8px; border-bottom: 1px solid #f3f4f6; padding-bottom: 6px; }
.tab { border: none; background: #f3f4f6; color: #111827; padding: 6px 10px; border-radius: 10px; font-weight: 700; }
.tab.active { background: #00274c; color: #fff; }
.ml-auto { margin-left: auto; }

/* Checkbox grid */
.checkbox-wrap { display: grid; grid-template-columns: 1fr 180px; gap: 12px; align-items: start; }
.checkbox-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 8px; border: 1px solid #d1d5db; border-radius: 12px; padding: 10px; max-height: 280px; overflow: auto; }
.checkbox-item { display: flex; align-items: center; gap: 10px; padding: 6px 8px; border-radius: 10px; transition: background-color .15s; }
.checkbox-item:hover { background: #f9fafb; }
.checkbox-input { width: 16px; height: 16px; accent-color: #00274c; }
.checkbox-label { font-size: .92rem; color: #374151; }
.select-actions { display: flex; flex-direction: column; gap: 8px; }
.w-full { width: 100%; text-align: center; }
.hint { font-size: .75rem; color: #6b7280; }
.chip { display: inline-block; background: #f3f4f6; border: 1px solid #e5e7eb; padding: 2px 8px; border-radius: 9999px; margin: 2px 4px 0 0; }

/* Toast */
.toast { position: fixed; top: 20px; right: 20px; background: #00274c; color: #fff; padding: 10px 12px; border-radius: 10px; display: flex; align-items: center; gap: 10px; min-width: 240px; box-shadow: 0 10px 25px rgba(0,0,0,.15); z-index: 100; }
.toast-icon { display: grid; place-items: center; }
.toast-content { flex: 1; }
.toast-close { background: transparent; border: none; color: #fff; }

/* Animations */
.fade-slide-enter-active, .fade-slide-leave-active { transition: opacity .15s ease, transform .15s ease; }
.fade-slide-enter, .fade-slide-leave-to { opacity: 0; transform: translateY(-10px); }

.slide-up-enter-active, .slide-up-leave-active { transition: transform .15s ease, opacity .15s ease; }
.slide-up-enter, .slide-up-leave-to { transform: translateY(16px); opacity: 0; }

.zoom-fade-enter-active, .zoom-fade-leave-active { transition: transform .12s ease-out, opacity .12s ease-out; }
.zoom-fade-enter, .zoom-fade-leave-to { transform: scale(.96); opacity: 0; }

/* Spinners */
.spinner { width: 34px; height: 34px; border-radius: 9999px; border: 3px solid #e5e7eb; border-top-color: #00274c; animation: spin 1s linear infinite; }
.spinner-sm { width: 16px; height: 16px; border-radius: 9999px; border: 2px solid #e5e7eb; border-top-color: #00274c; display: inline-block; vertical-align: middle; animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* Custom scrollbar */
.modal-body::-webkit-scrollbar { width: 6px; }
.modal-body::-webkit-scrollbar-track { background: transparent; }
.modal-body::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 999px; }
.modal-body::-webkit-scrollbar-thumb:hover { background: #9ca3af; }

.checkbox-grid::-webkit-scrollbar { width: 5px; }
.checkbox-grid::-webkit-scrollbar-track { background: transparent; }
.checkbox-grid::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 999px; }
.checkbox-grid::-webkit-scrollbar-thumb:hover { background: #9ca3af; }

/* ══════════════════════════════════════════════════════════════
   Dark Mode
   ══════════════════════════════════════════════════════════════ */

/* Title & muted text */
.theme--dark .title { color: #e4e5eb; }
.theme--dark .muted { color: #8b8d9e; }
.theme--dark .empty { color: #8b8d9e; }

/* Buttons */
.theme--dark .ghost { background: #1a1b23; color: #d5d6de; border-color: #2a2c3a; }
.theme--dark .ghost:hover { border-color: #ffd451; color: #ffd451; }
.theme--dark .btn-primary { background: #ffd451; color: #111318; }
.theme--dark .btn-primary:hover { opacity: .9; }
.theme--dark .danger { background: #dc2626; color: #fff; }
.theme--dark .danger:hover { opacity: .9; }

/* Cards */
.theme--dark .card { background: #1a1b23; border-color: #2a2c3a; box-shadow: 0 4px 10px rgba(0,0,0,.2); }
.theme--dark .text-main { color: #e4e5eb; }

/* Icon action buttons */
.theme--dark .icon-btn.view { background: #22232e; color: #d5d6de; }
.theme--dark .icon-btn.edit { background: #1a2744; color: #8eadff; }
.theme--dark .icon-btn.delete { background: #3a1a1a; color: #ff8a8a; }

/* Tags */
.theme--dark .tag-label { color: #8b8d9e; }
.theme--dark .tag { background: #22232e; border-color: #2a2c3a; color: #d5d6de; }
.theme--dark .tag--course { background: #1a2040; border-color: #2a3060; }
.theme--dark .tag--format { background: #1a2830; border-color: #2a3840; }
.theme--dark .tag--lang { background: #1a2820; border-color: #2a3830; }
.theme--dark .tag--paid { background: #2a2218; border-color: #3a3228; }
.theme--dark .tag--spec { background: #22232e; }

/* Detail drawer */
.theme--dark .drawer { background: #1a1b23; box-shadow: 0 10px 40px rgba(0,0,0,.5); }
.theme--dark .drawer-header h2 { color: #e4e5eb; }
.theme--dark .drawer-header .icon { color: #8b8d9e; }
.theme--dark .drawer-header .icon:hover { color: #e4e5eb; }
.theme--dark .kv span { color: #8b8d9e; }
.theme--dark .kv p { color: #e4e5eb; }
.theme--dark .hr { background: #2a2c3a; }
.theme--dark .chip { background: #22232e; border-color: #2a2c3a; color: #d5d6de; }

/* Modal */
.theme--dark .modal { background: rgba(0,0,0,.40); }
.theme--dark .backdrop { background: rgba(0,0,0,.40); }
.theme--dark .modal-card { background: #1a1b23; border-color: #2a2c3a; box-shadow: 0 10px 30px rgba(0,0,0,.4); }
.theme--dark .modal-header { border-bottom-color: #2a2c3a; }
.theme--dark .modal-header h3 { color: #e4e5eb; }
.theme--dark .modal-footer { border-top-color: #2a2c3a; }
.theme--dark .modal-body p { color: #d5d6de; }
.theme--dark .icon { color: #8b8d9e; }
.theme--dark .icon:hover { background: #22232e; color: #e4e5eb; }

/* Textarea */
.theme--dark .textarea { background: #22232e; border-color: #2a2c3a; color: #e4e5eb; }
.theme--dark .textarea:focus { border-color: #ffd451; }
.theme--dark .textarea::placeholder { color: #606275; }

/* Form labels & groups */
.theme--dark .label { color: #8b8d9e; }
.theme--dark .hint { color: #606275; }

/* Option pills */
.theme--dark .option { background: #22232e; color: #d5d6de; border-color: transparent; }
.theme--dark .option:not(.option--active):hover { border-color: #ffd451; color: #ffd451; }
.theme--dark .option--active { background: #ffd451; color: #111318; }
.theme--dark .option.option--ghost { background: #1a1b23; border-color: #2a2c3a; color: #d5d6de; }
.theme--dark .option.option--ghost:hover { border-color: #ffd451; }

/* Tabs (language selector) */
.theme--dark .tabs { border-bottom-color: #2a2c3a; }
.theme--dark .tab { background: #22232e; color: #d5d6de; }
.theme--dark .tab.active { background: #ffd451; color: #111318; }

/* Checkbox grid */
.theme--dark .checkbox-grid { border-color: #2a2c3a; }
.theme--dark .checkbox-item:hover { background: #22232e; }
.theme--dark .checkbox-label { color: #d5d6de; }
.theme--dark .checkbox-input { accent-color: #ffd451; }

/* Spinners */
.theme--dark .spinner { border-color: #2a2c3a; border-top-color: #ffd451; }
.theme--dark .spinner-sm { border-color: #2a2c3a; border-top-color: #ffd451; }

/* Toast (already dark, minor tweaks) */
.theme--dark .toast { background: #22232e; box-shadow: 0 10px 25px rgba(0,0,0,.4); }

/* Dark scrollbar */
.theme--dark .modal-body::-webkit-scrollbar-thumb { background: #33354a; }
.theme--dark .modal-body::-webkit-scrollbar-thumb:hover { background: #44465a; }
.theme--dark .checkbox-grid::-webkit-scrollbar-thumb { background: #33354a; }
.theme--dark .checkbox-grid::-webkit-scrollbar-thumb:hover { background: #44465a; }
</style>
